import React, { useState } from 'react';
import { Upload, Link, X, Image } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

const ImageUploader = ({ currentImage, onImageChange, label = "صورة المنتج" }) => {
  const [imageType, setImageType] = useState(() => {
    if (!currentImage) return 'emoji';
    if (currentImage.startsWith('http')) return 'url';
    if (currentImage.includes('firebasestorage.app')) return 'file';
    return 'emoji';
  });
  const [imageUrl, setImageUrl] = useState(currentImage?.startsWith('http') ? currentImage : '');
  const [imageEmoji, setImageEmoji] = useState(!currentImage?.startsWith('http') ? currentImage : '📦');

  const handleImageTypeChange = (type) => {
    setImageType(type);
    if (type === 'emoji') {
      onImageChange(imageEmoji);
    } else {
      onImageChange(imageUrl);
    }
  };

  const handleUrlChange = (url) => {
    setImageUrl(url);
    onImageChange(url);
  };

  const handleEmojiChange = (emoji) => {
    setImageEmoji(emoji);
    onImageChange(emoji);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // إنشاء اسم فريد للملف
        const fileName = `images/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, fileName);
        
        // رفع الصورة إلى Firebase Storage
        const snapshot = await uploadBytes(storageRef, file);
        
        // الحصول على رابط التحميل
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        setImageUrl(downloadURL);
        onImageChange(downloadURL);
        
        console.log('تم رفع الصورة بنجاح:', downloadURL);
      } catch (error) {
        console.error('خطأ في رفع الصورة:', error);
        alert('حدث خطأ أثناء رفع الصورة');
      }
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 mb-2 text-right">{label}</label>
      
      {/* Image Type Selector */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => handleImageTypeChange('emoji')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            imageType === 'emoji'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          رمز Emoji
        </button>
        <button
          type="button"
          onClick={() => handleImageTypeChange('file')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            imageType === 'file'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          رفع ملف
        </button>
        <button
          type="button"
          onClick={() => handleImageTypeChange('url')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            imageType === 'url'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          رابط أو ملف
        </button>
      </div>

      {/* Preview */}
      <div className="bg-gray-50 rounded-xl p-6 text-center border-2 border-dashed border-gray-300">
        <div className="text-6xl mb-2">
          {imageType === 'emoji' ? (
            imageEmoji
          ) : imageUrl ? (
            <img 
              src={imageUrl} 
              alt="Preview" 
              className="w-32 h-32 object-cover rounded-lg mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : (
            <Image className="w-32 h-32 mx-auto text-gray-300" />
          )}
          <div className="text-gray-400 text-sm mt-2" style={{ display: 'none' }}>صورة غير صالحة</div>
        </div>
        <p className="text-sm text-gray-500">معاينة</p>
      </div>

      {/* Input based on type */}
      {imageType === 'emoji' ? (
        <div>
          <input
            type="text"
            value={imageEmoji}
            onChange={(e) => handleEmojiChange(e.target.value)}
            placeholder="📦"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center text-4xl"
          />
          <p className="text-xs text-gray-500 text-center mt-2">
            اختر emoji من لوحة المفاتيح أو انسخ والصق
          </p>
        </div>
      ) : imageType === 'file' ? (
        <div className="space-y-3">
          {/* File Upload */}
          <div>
            <label className="block w-full cursor-pointer">
              <div className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-yellow-500 hover:bg-yellow-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700 font-medium">رفع صورة من الجهاز</span>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 text-right">
            💡 الصورة ستُحفظ في Firebase Storage وستحصل على رابط سريع التحميل
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* URL Input */}
          <div className="relative">
            <Link className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
            />
          </div>
          <p className="text-xs text-gray-500 text-right">
            الصق رابط الصورة من الإنترنت
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
